import React, { useEffect, useRef, useState } from 'react';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';
import { generateFilterKey } from '../../../utils/helper.jsx';
import useDebounce from '../../Shared/Delay/index.tsx';
import { campaignTableStateAtom } from '../../../Services/_states/index.js';
import { fetchCampaignListSelector } from '../../../Services/_states/CampaignPlanning/campaignSelector.ts';

interface CampaignAssignedDropdownProps {
  value: any;
  onChange: (value: any) => void;
  organisationId: string;
  error?: boolean;
  errorMessage?: string;
  label?: string;
  disabled?: boolean;
  sx?: any;
  autoSelectValue?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  // onCampaignsFetched?: (list: any[]) => void;

}

const CampaignAssignedDropdown: React.FC<CampaignAssignedDropdownProps> = ({
  value,
  onChange,
  error = false,
  errorMessage = '',
  label = 'Select Campaign',
  disabled = false,
  sx = { mb: 1, mt: 1 },
  organisationId,
  autoSelectValue = true,
  onOpen,
  onClose,
  // onCampaignsFetched,

}) => {
  // (showSnackBar is unused; you can remove useSnackBar import if not needed)
  const { showSnackBar } = useSnackBar();
  const [tableState, setTableState] = useRecoilState(campaignTableStateAtom);
  const fetchedData = useRecoilValueLoadable(fetchCampaignListSelector);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Write fresh API response to cache
  // useEffect(() => {
  //   if (fetchedData.state === 'hasValue' && !fetchedData.contents.fromCache) {
  //     const filterKey = generateFilterKey(tableState.filters);
  //     setTableState(prev => ({
  //       ...prev,
  //       cache: {
  //         ...prev.cache,
  //         [filterKey]: [
  //           ...(prev.cache[filterKey] || []),
  //           ...(fetchedData.contents.data || []),
  //         ],
  //       },
  //       totalCountCache: {
  //         ...prev.totalCountCache,
  //         [filterKey]: fetchedData.contents.totalCount || 0,
  //       },
  //     }));
  //   }
  // }, [fetchedData, setTableState, tableState.filters]);

  useEffect(() => {
    if (fetchedData.state === 'hasValue' && !fetchedData.contents.fromCache) {
      const filterKey = generateFilterKey(tableState.filters);
      setTableState(prev => ({
        ...prev,
        cache: {
          ...prev.cache,
          [filterKey]: fetchedData.contents.data || [],
        },
        totalCountCache: {
          ...prev.totalCountCache,
          [filterKey]: fetchedData.contents.totalCount || 0,
        },
      }));
    }
  }, [fetchedData, setTableState, tableState.filters]);


  // Update filters on org/search change; reset pagination + caches
  useEffect(() => {
    if (!organisationId) return;
    setTableState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        organisation_id: organisationId,
        search: debouncedSearchQuery,
        next_page: 0,
      },
      cache: {},
      totalCountCache: {},
    }));
  }, [organisationId, debouncedSearchQuery, setTableState]);

  // Compute options for the current search
  const currentSearch = tableState.filters.search || '';
  const campaignOptions =
    Object.entries(tableState.cache)
      .filter(([key]) => key.includes(`search=${currentSearch}`))
      .flatMap(([, val]: any) => val) || [];

  const uniqBy = <T, K extends keyof any>(arr: T[], key: (i: T) => K) => {
    const map = new Map<K, T>();
    arr.forEach(item => map.set(key(item), item));
    return Array.from(map.values());
  };

  // Notify parent when we have campaign options
  // useEffect(() => {
  //   if (!onCampaignsFetched) return;
  //   const list = uniqBy(campaignOptions, (i: any) => i?.proposal_id);
  //   onCampaignsFetched(list); 
  // }, [campaignOptions]);

  // Optional: read fresh list directly from selector result
  const getFreshList = () => {
    if (fetchedData.state !== 'hasValue') return [];
    const c = fetchedData.contents;
    // If your selector always normalizes to { data, totalCount }, this is enough:
    return Array.isArray(c?.data) ? c.data : [];
  };

  // Guard to avoid repeated auto-selects
  const autoSelectedRef = useRef(false);

  // ✅ Auto-select when exactly one option is available
  useEffect(() => {
    if (!autoSelectValue) return;
    if (disabled) return;
    if (autoSelectedRef.current) return;

    const isEmptyValue = !value || (typeof value === 'object' && Object.keys(value).length === 0);
    if (!isEmptyValue) return;

    // Prefer the cache (what the user sees); fall back to fresh payload on first paint
    let source = campaignOptions;
    if (source.length === 0) {
      source = getFreshList();
    }

    if (source.length === 1) {
      onChange(source[0]);
      autoSelectedRef.current = true;
    }
  }, [autoSelectValue, disabled, value, campaignOptions, fetchedData.state, fetchedData.contents, onChange]);

  // Pagination
  const loadMoreCampaign = async () => {
    const totalCount = tableState.totalCountCache[generateFilterKey(tableState.filters)] || 0;
    const allCachedValues = Object.entries(tableState.cache)
      .filter(([key]) => key.includes(`search=${currentSearch}`))
      .flatMap(([, val]: any) => val);

    if (allCachedValues.length < totalCount) {
      setTableState(prev => ({
        ...prev,
        filters: {
          ...prev.filters,
          next_page: prev.filters.next_page + 1,
        },
      }));
    }
  };

  // Search handler (local input only; the debounced effect updates filters & clears caches)
  const handleSearchCampaign = (query: string) => {
    setSearchQuery(query);
    setTableState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        search: query,
        next_page: 0,
      },
      cache: {},
      totalCountCache: {},
    }));
  };

  return (
    <SearchableDropdown
      options={campaignOptions}
      label={label}
      value={value}
      onChange={onChange}
      fetchMoreOptions={loadMoreCampaign}
      onSearch={handleSearchCampaign}
      searchValue={searchQuery}
      labelKey="name"
      valueKey="proposal_id"
      loading={fetchedData.state === 'loading'}
      error={error}
      errorMessage={errorMessage}
      disabled={disabled}
      sx={sx}
      onOpen={() => {
        onOpen?.();
      }}
      // onClose={() => {
      //   onClose?.();
      // }}
      onClose={() => {
        setSearchQuery('');
        setTableState(prev => ({
          ...prev,
          filters: {
            ...prev.filters,
            search: '',
            next_page: 0,
          },
          cache: {},
          totalCountCache: {},
        }));
        onClose?.();
      }}

    />
  );
};

export default CampaignAssignedDropdown;
